import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Activity, 
  Target, 
  Clock, 
  TrendingUp,
  Eye,
  Network,
  Map as MapIcon,
  Calendar,
  Lightbulb,
  RefreshCw
} from 'lucide-react';
import { analyticsEngine } from '../lib/analyticsEngine';
import { MetricCard } from '../components/analytics/MetricCard';
import { UsageHeatMap } from '../components/analytics/UsageHeatMap';
import { InsightCard } from '../components/analytics/InsightCard';
import { FrameworkConnectionChart } from '../components/analytics/FrameworkConnectionChart';
import { CapabilityGapChart } from '../components/analytics/CapabilityGapChart';
import { Timeline } from '../components/analytics/Timeline';
import { TeamLeaderboard } from '../components/team-widgets/TeamLeaderboard';
import { YourRankBadge } from '../components/team-widgets/YourRankBadge';
import type { AnalyticsData } from '../types/analytics';

export const Analytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'patterns' | 'connections' | 'gaps' | 'timeline'>('overview');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = () => {
    setLoading(true);
    try {
      const data = analyticsEngine.generateAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  if (loading || !analytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-consulting-slate-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-charcoal-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-charcoal-900 mb-2">Analytics Dashboard</h1>
          <p className="text-charcoal-600">
            Insights into your learning journey and system exploration patterns
          </p>
        </div>
        <button
          onClick={loadAnalytics}
          className="flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 text-white transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Team Comparison Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TeamLeaderboard 
            metric="exploration"
            maxDisplay={10}
            showYourRank={true}
          />
        </div>
        <div>
          <YourRankBadge metric="exploration" compact={false} />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Visits"
          value={analytics.totalVisits}
          subtitle="Pages viewed"
          icon={<Eye className="h-6 w-6 text-consulting-slate-blue-600" />}
          color="primary"
        />
        <MetricCard
          title="Unique Pages"
          value={analytics.uniquePagesVisited}
          subtitle="Different pages explored"
          icon={<MapIcon className="h-6 w-6 text-sage-600" />}
          color="sage"
        />
        <MetricCard
          title="Time Spent"
          value={formatTime(analytics.totalTimeSpent)}
          subtitle="Total exploration time"
          icon={<Clock className="h-6 w-6 text-[var(--mustard)]" />}
          color="amber"
        />
        <MetricCard
          title="Avg Session"
          value={formatTime(analytics.averageSessionLength)}
          subtitle="Per page visit"
          icon={<Activity className="h-6 w-6 text-coral-600" />}
          color="coral"
        />
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-[var(--rule)]">
        <nav className="flex gap-6">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'patterns', label: 'Usage Patterns', icon: Activity },
            { id: 'connections', label: 'Connections', icon: Network },
            { id: 'gaps', label: 'Coverage', icon: Target },
            { id: 'timeline', label: 'Timeline', icon: Calendar },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`
                flex items-center gap-2 px-1 py-3 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id
                  ? 'border-consulting-slate-blue-600 text-consulting-slate-blue-700'
                  : 'border-transparent text-charcoal-600 hover:text-charcoal-900'
                }
              `}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="animate-fadeIn">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Meta Insights */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="h-6 w-6 text-[var(--mustard)]" />
                <h2 className="text-2xl font-bold text-charcoal-900">Key Insights</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {analytics.metaInsights.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </div>
            </section>

            {/* Top Frameworks by Visits */}
            <section>
              <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Most Visited Pages</h2>
              <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
                <div className="space-y-3">
                  {analytics.pageVisits
                    .sort((a, b) => b.visitCount - a.visitCount)
                    .slice(0, 10)
                    .map((page, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-[var(--rule)] last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-charcoal-500 w-6">#{idx + 1}</span>
                          <div>
                            <p className="font-medium text-charcoal-900">{page.path}</p>
                            <p className="text-xs text-charcoal-600">{page.category}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-consulting-slate-blue-700">{page.visitCount} visits</p>
                          <p className="text-xs text-charcoal-600">{formatTime(page.totalTimeSpent)}</p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'patterns' && (
          <div className="space-y-8">
            {/* Usage Heat Map */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Activity className="h-6 w-6 text-consulting-slate-blue-600" />
                <h2 className="text-2xl font-bold text-charcoal-900">Usage Heat Map</h2>
              </div>
              <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
                <p className="text-sm text-charcoal-600 mb-6">
                  When you're most active in the knowledge system
                </p>
                <UsageHeatMap data={analyticsEngine.getHeatMapData()} />
              </div>
            </section>

            {/* Exploration Paths */}
            {analytics.explorationPaths.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="h-6 w-6 text-sage-600" />
                  <h2 className="text-2xl font-bold text-charcoal-900">Common Exploration Paths</h2>
                </div>
                <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
                  <div className="space-y-4">
                    {analytics.explorationPaths.map((path, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-4 bg-[var(--surface)]">
                        <span className="text-2xl font-bold text-charcoal-400">#{idx + 1}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-sm font-medium text-charcoal-900">
                            {path.sequence.map((step, stepIdx) => (
                              <React.Fragment key={stepIdx}>
                                <span className="px-3 py-1 bg-[var(--bg-raised)] rounded-full border border-[var(--rule)]">
                                  {step}
                                </span>
                                {stepIdx < path.sequence.length - 1 && (
                                  <span className="text-charcoal-400">→</span>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                          <p className="text-xs text-charcoal-600 mt-2">
                            Frequency: {path.frequency} | Avg: {formatTime(path.avgSessionLength)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </div>
        )}

        {activeTab === 'connections' && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Network className="h-6 w-6 text-consulting-slate-blue-600" />
              <h2 className="text-2xl font-bold text-charcoal-900">Framework Connections</h2>
            </div>
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
              <p className="text-sm text-charcoal-600 mb-6">
                Frameworks ranked by their connectivity within the knowledge system
              </p>
              <FrameworkConnectionChart data={analytics.frameworkConnections} limit={15} />
            </div>
          </section>
        )}

        {activeTab === 'gaps' && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-6 w-6 text-sage-600" />
              <h2 className="text-2xl font-bold text-charcoal-900">Knowledge Coverage</h2>
            </div>
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
              <p className="text-sm text-charcoal-600 mb-6">
                Your exploration progress across different MOF layers
              </p>
              <CapabilityGapChart data={analytics.capabilityGaps} />
            </div>
          </section>
        )}

        {activeTab === 'timeline' && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-6 w-6 text-[var(--mustard)]" />
              <h2 className="text-2xl font-bold text-charcoal-900">Journey Timeline</h2>
            </div>
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
              <p className="text-sm text-charcoal-600 mb-6">
                Your exploration milestones and achievements over time
              </p>
              <Timeline events={analytics.timeline} />
            </div>
          </section>
        )}
      </div>

      {/* Last Updated */}
      <div className="text-center text-sm text-charcoal-500">
        Last updated: {new Date(analytics.lastUpdated).toLocaleString()}
      </div>
    </div>
  );
};

